import { useCallback } from 'react';
import { useColoRequest } from 'hooks/useApi/useColoRequest';
import { toCents } from 'util/Formatting';
import { getCloverApiHostname } from 'util/Environment';

type WebPayTokenResponse = {
  token: string;
};

const baseURL = getCloverApiHostname();

export function useApplePayCosRequests() {
  const [, validateMerchant] = useColoRequest((validationUrl: string, token: string) => ({
    method: 'POST',
    baseURL,
    url: '/v3/apple_pay_session',
    data: {
      url: validationUrl,
      token
    },
  }));

  const [, payPublicly] = useColoRequest((
    event: ApplePayJS.ApplePayPaymentAuthorizedEvent,
    receiptId: string,
    token: string,
    tipAmount: number
  ) => {
    const { paymentData, paymentMethod, transactionIdentifier } = event.payment.token;
    const { addressLines, postalCode } = event.payment.billingContact ?? {};

    return ({
      method: 'POST',
      baseURL,
      url: `/v3/${receiptId}/public_pay`,
      data: {
        token,
        applePayRequest: {
          applePayPaymentData: paymentData,
          paymentMethod,
          transactionIdentifier,
          contactInfo: { postalCode, address: addressLines?.join(' ') },
          tipAmount: toCents(tipAmount),
        },
        paymentAttributes: {
          source_type: 'colo',
          payment_platform: 'apple_pay',
        },
      }
    });
  });

  const [, getWebPayTokenImpl] = useColoRequest((receiptId: string) => ({
    baseURL,
    url: `/v3/${receiptId}/web_pay_token`
  }));

  const getWebPayToken = useCallback(async (receiptId: string) => {
    const response = await getWebPayTokenImpl<WebPayTokenResponse>(receiptId);

    if (!response?.token) return;

    return response.token;
  }, [getWebPayTokenImpl]);

  return { validateMerchant, payPublicly, getWebPayToken };
}
